import type { CSSProperties } from 'react';
import { Gift, Lock, Unlock } from 'lucide-react';
import {
  deckStatus,
  formatDeckDate,
  formatEuros,
  skillcardAccessLabel,
  type CardPalette,
  type SkillcardAccess,
} from '@jeraisonne/db/cards';
import { isNativeApp } from '@/lib/platform';
import { canBuySkillcardsInApp } from '@/lib/skillcards/iap';
import { skillcardBuyPath, useSkillcardOffers } from '@/lib/skillcards/purchase';

/**
 * Bloc « accès » de la page d'une SKILLCARD (migration 0304) : carte débloquée
 * (raison + fin du deck) ou bouton vers la page d'achat (carte / pack /
 * programme). Masqué pendant le chargement. Carte verrouillée : aux couleurs
 * de la carte (`palette`), comme les offres de la page d'achat, avec le prix
 * d'entrée en grand (« à partir de 1,90 € »).
 */
export function SkillcardUnlockPanel({
  cardId,
  userId,
  access,
  palette,
}: {
  cardId: string;
  userId: string | null;
  access: SkillcardAccess | undefined;
  /** Teinte de la carte (`cardPalette`). */
  palette: CardPalette;
}) {
  const offers = useSkillcardOffers(cardId, userId);
  if (!access) return null;

  if (access.allowed) {
    const label = skillcardAccessLabel(access.reason);
    const deck = deckStatus(access.deck_expires_at);
    return (
      <div className="bg-success-50 text-success-800 flex items-start gap-3 rounded-2xl p-4 text-sm">
        <Unlock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p>
            <strong>{label ?? 'Débloquée'}.</strong>
            {access.reason === 'skillcard' && deck.active && access.deck_expires_at && (
              <> Ton deck est actif jusqu’au {formatDeckDate(access.deck_expires_at)}.</>
            )}
          </p>
          {/* Offrir : la carte cadeau vit sur la page d'achat (web seulement). */}
          {userId && !isNativeApp() && (
            <a
              href={`${skillcardBuyPath(cardId)}#offrir`}
              className="text-success-800 -mb-2 inline-flex min-h-[2.75rem] items-center gap-1.5 font-semibold underline underline-offset-2"
            >
              <Gift className="h-4 w-4" aria-hidden /> Offrir cette SKILLCARD
            </a>
          )}
        </div>
      </div>
    );
  }

  const card = offers.data?.card;
  const fromPrice = card && card.cards_to_buy > 0 ? formatEuros(card.price_cents) : null;
  // Teinte de la carte en variables CSS (seules valeurs non statiques).
  const vars = { '--jr-o-accent': palette.accent, '--jr-o-bg': palette.bg } as CSSProperties;
  return (
    <div
      style={vars}
      data-testid="skillcard-unlock-panel"
      className="space-y-3 rounded-2xl border border-[var(--jr-o-accent)] bg-[linear-gradient(135deg,color-mix(in_srgb,var(--jr-o-accent)_22%,var(--jr-o-bg))_0%,var(--jr-o-bg)_75%)] p-4 text-white ring-2 ring-[color-mix(in_srgb,var(--jr-o-accent)_35%,transparent)]"
    >
      <div className="flex items-start gap-3">
        <Lock className="mt-1 h-4 w-4 shrink-0 text-[var(--jr-o-accent)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-display text-xs font-extrabold uppercase tracking-wider text-[var(--jr-o-accent)]">
            Débloque ta SKILLCARD
          </p>
          <p className="mt-1 text-sm text-white/80">
            Chaque niveau a un essai gratuit (1 question). Débloque la carte pour tous ses exercices
            pendant 1 an
            {access.deck_expires_at && deckStatus(access.deck_expires_at).active === false
              ? ' — ton deck précédent a expiré, un achat le réactive.'
              : '.'}
          </p>
        </div>
        {fromPrice && canBuySkillcardsInApp() && (
          <p className="shrink-0 text-right leading-none">
            <span className="block text-xs text-white/70">à partir de</span>
            <span className="font-display text-3xl font-extrabold text-white">{fromPrice}</span>
          </p>
        )}
      </div>
      {!canBuySkillcardsInApp() ? (
        <p className="text-xs text-white/70">
          Achat sur jeraisonne.fr, ou inclus dans l’abonnement JeRaisonne.
        </p>
      ) : (
        <a
          href={skillcardBuyPath(cardId)}
          className="font-display flex min-h-[2.75rem] w-full items-center justify-center rounded-xl bg-[var(--jr-o-accent)] px-4 text-sm font-bold text-[var(--jr-o-bg)] shadow hover:brightness-110 active:scale-[0.98]"
        >
          Voir les offres
        </a>
      )}
    </div>
  );
}
